/**
 * TrustStrip — landing-page horizontal credibility strip
 *
 * Extracted from Landing.js (Jun 2026) to reduce the monolith by ~63 lines.
 * Pure presentational component — no props, no state. Reads test IDs
 * remain identical to the inlined version so Playwright suites don't break.
 *
 * Pills (left → right):
 *   - Zero Data Retention   → /security
 *   - SOC 2 trust badge     → SOC2TrustBadge component (lives elsewhere)
 *   - GDPR Compliant        → /privacy-policy
 *   - 132 Math Tests Passing → /algorithm-correctness
 *   - Wall (live tenants)   → /wall-of-tenants
 *   - 6 cloud integrations  → /integrations
 *   - 99.97% Proxy Uptime   → /system-status
 *   - 0 Auto-PR Outages     → /security
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileCheck, ArrowRight, Activity, ShieldCheck } from 'lucide-react';
import SOC2TrustBadge from '../SOC2TrustBadge';

export default function TrustStrip() {
  return (
    <section className="border-y border-white/5 bg-white/[0.02]">
      <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-center gap-5 flex-wrap">
        <Link
          to="/security"
          className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors group"
          data-testid="trust-zdr"
        >
          <Shield className="w-4 h-4 text-emerald-400" />
          <span className="text-xs">Zero Data Retention</span>
        </Link>
        <SOC2TrustBadge />
        <Link
          to="/privacy-policy"
          className="flex items-center gap-2 text-slate-400 hover:text-purple-300 transition-colors group"
          data-testid="trust-gdpr"
        >
          <Shield className="w-4 h-4 text-purple-400" />
          <span className="text-xs">GDPR Compliant</span>
        </Link>
        <Link
          to="/algorithm-correctness"
          className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors group"
          data-testid="trust-math-tests"
        >
          <FileCheck className="w-4 h-4 text-emerald-400" />
          <span className="text-xs">
            <span className="text-emerald-300 font-semibold tabular-nums">132</span> Math Tests Passing
          </span>
        </Link>
        <Link
          to="/wall-of-tenants"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors group"
          data-testid="trust-wall-of-tenants"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-xs">
            <span className="text-emerald-300 font-semibold">Wall</span> · watch tenants saving live{' '}
            <ArrowRight className="w-3 h-3 inline ml-0.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>
        <Link to="/integrations" className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">
          6 cloud integrations live →
        </Link>
        <Link
          to="/system-status"
          className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors"
          data-testid="trust-uptime"
        >
          <Activity className="w-4 h-4 text-emerald-400" />
          <span className="text-xs">
            <span className="text-emerald-300 font-semibold tabular-nums">99.97%</span> Proxy Uptime
          </span>
        </Link>
        <Link
          to="/security"
          className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors"
          data-testid="trust-zero-outages"
        >
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span className="text-xs">
            <span className="text-emerald-300 font-semibold">0</span> Auto-PR Outages
          </span>
        </Link>
      </div>
    </section>
  );
}
